// pages/LessonPage.jsx
import { useParams, useHistory } from "react-router";
import { useEffect, useState, useMemo } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Typography,
  RadioGroup,
  FormControlLabel,
  Radio,
  IconButton,
  Stack,
  Paper,
  Alert,
  Container,
} from "@mui/material";
import { styled, alpha } from "@mui/material/styles";
import ReactPlayer from "react-player";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import OpenInNewRounded from "@mui/icons-material/OpenInNewRounded";
import PictureAsPdfRounded from "@mui/icons-material/PictureAsPdfRounded";
import ProgressBar from "./Lesson/ProgressBar";
import axios from "axios";
import customAxios from "lib/axios";
import { useTranslation } from "react-i18next";

const BRAND = "#253e66";

const Shell = styled(Container)(({ theme }) => ({
  paddingTop: theme.spacing(3),
  paddingBottom: theme.spacing(3),
}));
const Header = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
}));
const Card = styled(Paper)(({ theme }) => ({
  width: "100%",
  maxWidth: 900,
  borderRadius: 16,
  padding: theme.spacing(3),
  boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
  background: "#fff",
}));
const Row = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
}));
const NavButton = styled(IconButton)(({ theme }) => ({
  borderRadius: 999,
  background: alpha(BRAND, 0.08),
  color: BRAND,
  "&:hover": { background: alpha(BRAND, 0.16) },
}));
const PillButton = styled(Button)(({ theme }) => ({
  borderRadius: 999,
  textTransform: "none",
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(2),
}));
const MediaBox = styled(Box)(({ theme }) => ({
  width: "100%",
  maxWidth: 860,
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(2),
}));
const QuestionCard = styled(Paper)(({ theme }) => ({
  width: "100%",
  padding: theme.spacing(2),
  borderRadius: 12,
  background: alpha(BRAND, 0.04),
  border: `1px solid ${alpha(BRAND, 0.12)}`,
}));

const LessonPage = () => {
  const history = useHistory();
  const { courseId, moduleId, lessonId, pageNumber } = useParams();
  const { t, i18n } = useTranslation("courses");

  const isEn = String(i18n.resolvedLanguage || i18n.language || "es")
    .toLowerCase()
    .startsWith("en");
  const pick = (es, en) => (isEn ? en ?? es : es);

  const [title, setTitle] = useState("");
  const [texts, setTexts] = useState([]);
  const [descriptions, setDescriptions] = useState([]);
  const [images, setImages] = useState([]);
  const [videos, setVideos] = useState([]);
  const [redirectUrl, setRedirectUrl] = useState([]); // arrays
  const [documentUrl, setDocumentUrl] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState("");
  const [correctAnswers, setCorrectAnswers] = useState([]);
  const [answeredPages, setAnsweredPages] = useState([]);
  const [errorMessage, setErrorMessage] = useState("");
  const userId = localStorage.getItem("user_id");

  const initialIndex = pageNumber ? parseInt(pageNumber, 10) - 1 : 0;
  const [pageIndex, setPageIndex] = useState(initialIndex);
  const [maxReached, setMaxReached] = useState(initialIndex);
  const DOC_BASE = process.env.REACT_APP_DOCCURSO || "";

  const safeIndex = useMemo(
    () => Math.min(Math.max(pageIndex, 0), Math.max(texts.length - 1, 0)),
    [pageIndex, texts.length]
  );

  const hasTest = Boolean(redirectUrl[safeIndex] || questions[safeIndex]);

  useEffect(() => {
    if (pageNumber) {
      const idx = parseInt(pageNumber, 10) - 1;
      if (!isNaN(idx) && idx !== pageIndex) setPageIndex(idx);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageNumber]);

  useEffect(() => {
    setPageIndex(0);
    setMaxReached(0);
  }, [lessonId]);

  // Fetch páginas y respuestas
  useEffect(() => {
    let isMounted = true;
    const source = axios.CancelToken.source();

    const fetchPages = async () => {
      try {
        const { data: lessonData } = await customAxios.get(
          `/lessons/one/${lessonId}`,
          { cancelToken: source.token }
        );
        if (isMounted) setTitle(pick(lessonData.title, lessonData.titleEN));

        const { data: rawPages } = await customAxios.get(`/pages/${lessonId}`, {
          cancelToken: source.token,
        });

        const pageCorrect = rawPages.map((p) => p.answer);
        setCorrectAnswers(pageCorrect);

        const { data: answerData } = await customAxios.get(`/answerOptions`);

        // Agrupar opciones por página (con textos en ES/EN)
        const grouped = rawPages.map((page) => {
          const opts = answerData
            .filter((o) => o.pageId === page.id || o.page_id === page.id)
            .map((o) => {
              const key = o.option_key || o.optionKey;
              const textES = o.option_text ?? o.optionText;
              const textEN = o.option_text_EN ?? o.optionTextEN;
              return { key, text: pick(textES, textEN) };
            });
          return opts.sort((a, b) =>
            String(a.key).localeCompare(String(b.key))
          );
        });

        if (!isMounted) return;

        // Arrays por campo con ES/EN
        setAnswers(grouped);
        setTexts(rawPages.map((p) => pick(p.text, p.textEN)));
        setDescriptions(
          rawPages.map((p) => pick(p.description, p.descriptionEN))
        );
        setImages(rawPages.map((p) => pick(p.image, p.imageEN)));
        setVideos(rawPages.map((p) => pick(p.video, p.videoEN)));
        setRedirectUrl(
          rawPages.map((p) => pick(p.redirectUrl, p.redirectUrlEN))
        );
        setDocumentUrl(
          rawPages.map((p) => pick(p.documentUrl, p.documentUrlEN))
        );
        setQuestions(rawPages.map((p) => pick(p.question, p.questionEN)));
        setAnsweredPages(rawPages.map(() => false));
      } catch (err) {
        if (!customAxios.isCancel(err) && isMounted) setError(err);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchPages();
    return () => {
      isMounted = false;
      source.cancel();
    };
  }, [lessonId, isEn]); // si cambia el idioma, rehace el pick

  // Guardar progreso y volver al módulo expandido
  const saveProgressAndRedirect = async () => {
    await customAxios.post(`/userCourseProgress/user/lesson/${lessonId}`, {
      completed: 1,
    });

    const { data: allLessons } = await customAxios.get(`/lessons/${moduleId}`);
    const { data: doneLessons } = await customAxios.get(
      `/userCourseProgress/user/module/${moduleId}`
    );

    if (doneLessons.length >= allLessons.length) {
      await customAxios.post(`/userCourseProgress/user/module/${moduleId}`, {
        completed: 1,
      });
    }
    history.push(`/myCourses/${courseId}`, {
      expandModuleId: Number(moduleId),
    });
  };

  const handleVerify = async () => {
    if (!questions[safeIndex]) {
      if (safeIndex === texts.length - 1) {
        await saveProgressAndRedirect();
        return;
      }
      handleIncrement();
      return;
    }

    if (selected === String(correctAnswers[safeIndex])) {
      setErrorMessage("");
      setAnsweredPages((prev) => {
        const next = [...prev];
        next[safeIndex] = true;
        return next;
      });
      if (safeIndex === texts.length - 1) {
        await saveProgressAndRedirect();
      } else {
        handleIncrement();
      }
    } else {
      setErrorMessage(t("wrong_answer"));
    }
  };

  const handleIncrement = () => {
    if (safeIndex >= texts.length - 1) return;
    const newIndex = safeIndex + 1;
    setMaxReached((prev) => Math.max(prev, newIndex));
    setSelected("");
    setErrorMessage("");
    history.push(
      `/myCourses/${courseId}/modules/${moduleId}/lesson/${lessonId}/page/${
        newIndex + 1
      }`
    );
  };

  const handleDecrement = () => {
    if (safeIndex <= 0) return;
    const newIndex = safeIndex - 1;
    setSelected("");
    setErrorMessage("");
    history.push(
      `/myCourses/${courseId}/modules/${moduleId}/lesson/${lessonId}/page/${
        newIndex + 1
      }`
    );
  };

  const handleSelect = (event) => setSelected(event.target.value);

  if (loading)
    return <CircularProgress sx={{ display: "block", mx: "auto", mt: 8 }} />;
  if (error)
    return (
      <Typography color="error" textAlign="center" sx={{ mt: 8 }}>
        {t("error_loading")}
      </Typography>
    );

  const userCanAdvance = !questions[safeIndex] || answeredPages[safeIndex];

  return (
    <Shell maxWidth="lg">
      {/* Header */}
      <Header>
        <IconButton
          aria-label={t("back")}
          onClick={() =>
            history.push(`/myCourses/${courseId}`, {
              expandModuleId: Number(moduleId),
            })
          }
          size="large"
        >
          <ArrowBackIcon />
        </IconButton>

        <Typography variant="h5" textAlign="center" sx={{ flexGrow: 1 }}>
          {title}
        </Typography>

        <Box width={48} />
      </Header>

      {/* Controles + barra */}
      <Row sx={{ gap: 1, mb: 2 }}>
        <NavButton
          onClick={handleDecrement}
          aria-label={t("prev")}
          disabled={safeIndex <= 0}
        >
          <KeyboardArrowLeftIcon />
        </NavButton>

        <Box sx={{ width: { xs: "100%", sm: 480, md: 600 } }}>
          <ProgressBar
            currentIndex={safeIndex}
            maxReached={maxReached}
            total={texts.length}
          />
        </Box>

        <NavButton
          onClick={() => userCanAdvance && handleIncrement()}
          aria-label={t("next")}
          disabled={!userCanAdvance || safeIndex >= texts.length - 1}
        >
          <KeyboardArrowRightIcon />
        </NavButton>
      </Row>

      {/* Tarjeta principal */}
      <Row>
        <Card>
          <Stack spacing={1} alignItems="center" textAlign="center">
            <Typography variant="h6">{texts[safeIndex]}</Typography>
            {descriptions[safeIndex] && (
              <Typography
                variant="body1"
                color="text.secondary"
                sx={{
                  maxWidth: 760,
                  display: "-webkit-box",
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                }}
              >
                {descriptions[safeIndex]}
              </Typography>
            )}
          </Stack>

          {/* Media */}
          {images[safeIndex] && (
            <MediaBox sx={{ maxWidth: hasTest ? 420 : 860, mx: "auto" }}>
              <img
                src={images[safeIndex]}
                alt={t("image_alt", { index: safeIndex + 1 })}
                style={{
                  display: "block",
                  margin: "0 auto",
                  width: "100%",
                  height: "auto",
                  borderRadius: 12,
                  boxShadow: "0 6px 18px rgba(0,0,0,0.08)",
                }}
              />
            </MediaBox>
          )}

          {videos[safeIndex] && (
            <MediaBox sx={{ position: "relative", paddingTop: "56.25%" }}>
              <ReactPlayer
                url={videos[safeIndex]}
                controls
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  borderRadius: 12,
                  overflow: "hidden",
                }}
                width="100%"
                height="100%"
              />
            </MediaBox>
          )}

          {/* Acciones extra */}
          {(redirectUrl[safeIndex] || documentUrl[safeIndex]) && (
            <Row sx={{ gap: 1, mb: 1 }}>
              {redirectUrl[safeIndex] && (
                <PillButton
                  variant="contained"
                  color="primary"
                  component="a"
                  href={redirectUrl[safeIndex]}
                  target="_blank"
                  rel="noopener noreferrer"
                  startIcon={<OpenInNewRounded />}
                >
                  {t("start_test")}
                </PillButton>
              )}

              {documentUrl[safeIndex] && (
                <PillButton
                  variant="outlined"
                  component="a"
                  href={`${DOC_BASE}${documentUrl[safeIndex]}`}
                  download
                  target="_blank"
                  rel="noopener noreferrer"
                  startIcon={<PictureAsPdfRounded />}
                >
                  {t("download_pdf")}
                </PillButton>
              )}
            </Row>
          )}

          <Divider sx={{ my: 2 }} />

          {/* Preguntas */}
          {questions[safeIndex] ? (
            <QuestionCard>
              <Typography variant="subtitle1" gutterBottom>
                {questions[safeIndex]}
              </Typography>
              <RadioGroup
                value={selected}
                onChange={(e) => setSelected(e.target.value)}
              >
                {answers[safeIndex]?.map((opt) => (
                  <FormControlLabel
                    key={opt.key}
                    value={String(opt.key)}
                    control={<Radio />}
                    label={opt.text}
                  />
                ))}
              </RadioGroup>

              {errorMessage && (
                <Alert severity="error" sx={{ mt: 1 }}>
                  {errorMessage}
                </Alert>
              )}

              <Row sx={{ mt: 2 }}>
                <PillButton
                  variant="contained"
                  color="primary"
                  onClick={handleVerify}
                  disabled={!!questions[safeIndex] && selected === ""}
                >
                  {safeIndex === texts.length - 1
                    ? t("finalize")
                    : t("verify_and_next")}
                </PillButton>
              </Row>
            </QuestionCard>
          ) : (
            <Row sx={{ mt: 1 }}>
              <PillButton
                variant="contained"
                color="primary"
                onClick={handleVerify}
              >
                {safeIndex === texts.length - 1 ? t("finalize") : t("advance")}
              </PillButton>
            </Row>
          )}
        </Card>
      </Row>
    </Shell>
  );
};

export default LessonPage;
